import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { FlightTakeoff as TrackIcon } from '@mui/icons-material';
import { FileCard } from '../file-browser/styles';
import FileCardBody from '../file-browser/FileCardBody';
import { formatDate } from '../file-browser/utils';
import { useTranslation } from '../../i18n';
import {
  entryLabel,
  entrySizeLabel,
  entryTooltipKey,
  isTrackEntry,
  type SharedEntry,
} from './sharedViewModel';

interface SharedEntryCardProps {
  entry: SharedEntry;
  /** Is this card part of the selection? Same ring as the Files browser's. */
  selected: boolean;
  /** Single click — apply selection, reading Ctrl/Cmd/Shift off the event. */
  onClick: (entry: SharedEntry, event: React.MouseEvent<HTMLElement>) => void;
  /** Double-click — the primary action: open a folder, download a file. */
  onOpen: (entry: SharedEntry) => void;
  onContextMenu: (event: React.MouseEvent<HTMLElement>, entry: SharedEntry) => void;
  /**
   * Does this entry, on its own, have anything in its menu? `false` draws no ⋮
   * button: a grantee's DroneLogbook track has nothing to act on (#1692).
   */
  hasMenu: boolean;
}

/**
 * One shared thing, rendered as a file card.
 *
 * Same `FileCard` and same `FileCardBody` as the browser's own entries — the
 * point of #1247 is that provenance changes the OWNER and the PERMISSION, not
 * the appearance — and since #1692 the same selection: click, Ctrl/Cmd-click,
 * Shift-click and the marquee, keyed on `data-entry-id`. What it does not have
 * is drag, drop and clipboard: those act on a tree this organization can write
 * to, and a shared entry sits in somebody else's.
 *
 * The label comes from `entryLabel`, the one place an entry without a file
 * name is named.
 */
const SharedEntryCard: React.FC<SharedEntryCardProps> = ({
  entry,
  selected,
  onClick,
  onOpen,
  onContextMenu,
  hasMenu,
}) => {
  const { t } = useTranslation();
  const track = isTrackEntry(entry);
  const tooltipKey = entryTooltipKey(entry);

  // Flights show when the aircraft flew; files show where they sit inside what
  // was shared. Both answer "which one is this?" for their own kind.
  const primaryMeta = entry.flightDate ? formatDate(entry.flightDate) : entry.parentPath ?? '';

  const card = (
    <FileCard
      isFolder={entry.isFolder}
      selected={selected}
      data-testid="shared-entry-card"
      data-entry-key={entry.key}
      // What the marquee hit-tests and the selection keys on.
      data-entry-id={entry.key}
      data-entry-kind={track ? 'dlb-track' : entry.kind}
      aria-selected={selected}
      onClick={(e) => {
        e.stopPropagation();
        onClick(entry, e);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onOpen(entry);
      }}
      onContextMenu={(e) => onContextMenu(e, entry)}
    >
      <FileCardBody
        name={entryLabel(entry, t)}
        isFolder={entry.isFolder}
        // A track has no file, so no file badge: the badge says "CSV" or a file
        // icon, which is the claim the relabel exists to stop making.
        icon={
          track ? (
            <TrackIcon sx={{ fontSize: 16 }} data-testid="shared-entry-track-icon" />
          ) : undefined
        }
        // A shared FOLDER has a permission and a place inside what was shared,
        // and both matter as much as they do for a file. The browser's own
        // folders do not, which is why this is opt-in rather than the default.
        showMetaForFolder
        primaryMeta={primaryMeta}
        secondaryMeta={entrySizeLabel(entry) ?? ''}
        extraMeta={
          <>
            {entry.droneSerial && (
              <Chip
                size="small"
                label={t('sharedView.droneChip', { serial: entry.droneSerial })}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={
                entry.permission === 'write'
                  ? t('sharedView.permissionWrite')
                  : t('sharedView.permissionRead')
              }
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
            {/* When the grant was made. Both deleted views showed this, and a
                non-admin has no other route to it — they cannot open the grant
                list. Losing it was the regression #1229's date fix was carried
                in this same pass to serve. */}
            {entry.grantedAt && (
              <Chip
                size="small"
                variant="outlined"
                data-testid="shared-entry-granted-chip"
                label={t('sharedView.grantedOn', { date: formatDate(entry.grantedAt) })}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
            {entry.revokedAt && (
              <Chip
                size="small"
                variant="outlined"
                data-testid="shared-entry-revoked-chip"
                label={t('sharedView.revokedOn', { date: formatDate(entry.revokedAt) })}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </>
        }
        onMore={hasMenu ? (e) => onContextMenu(e, entry) : undefined}
      />
    </FileCard>
  );

  return tooltipKey ? (
    <Tooltip title={t(tooltipKey)} describeChild>
      {card}
    </Tooltip>
  ) : (
    card
  );
};

export default SharedEntryCard;
